import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  setCustomerBlocked,
  useCustomersState,
  type CustomerRow,
} from "@/lib/admin/customers";
import { PageHeader } from "@/components/admin/PageHeader";
import { useAdminMoney } from "@/lib/admin/settings";
import { downloadCsv } from "@/lib/admin/analytics/data";
import { useAdminOrders } from "@/lib/admin/orders.cloud";
import { logAction } from "@/lib/admin/audit/log";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/customers")({ component: CustomersPage });

const DASH = "—";

/** Consistent Admin Console date format; never renders "Invalid Date". */
function fmtDate(value?: string | null) {
  if (!value) return DASH;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return DASH;
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Readable support-friendly phone formatting (keeps original digits). */
function fmtPhone(raw?: string | null) {
  if (!raw) return DASH;
  const trimmed = String(raw).trim();
  if (!trimmed) return DASH;
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 10) return `${digits.slice(0, 5)} ${digits.slice(5)}`;
  if (digits.length === 12 && digits.startsWith("91"))
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  if (digits.length === 11 && digits.startsWith("0"))
    return `${digits.slice(1, 6)} ${digits.slice(6)}`;
  return trimmed;
}

async function copyText(value: string, label: string) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  } catch {
    toast.error(`Could not copy ${label.toLowerCase()}`);
  }
}

const BLOCK_REASONS = [
  "Fraudulent activity",
  "Repeated fake orders",
  "Chargeback abuse",
  "Spam",
  "Policy violation",
  "Other",
];

function StatusBadge({ blocked }: { blocked: boolean }) {
  return (
    <span
      className={
        "inline-block whitespace-nowrap border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] " +
        (blocked
          ? "border-red-600/30 bg-red-50 text-red-700"
          : "border-emerald-600/30 bg-emerald-50 text-emerald-700")
      }
    >
      {blocked ? "Blocked" : "Active"}
    </span>
  );
}

function CustomersPage() {
  const { rows, reload } = useCustomersState();
  const money = useAdminMoney();
  const [q, setQ] = useState("");
  const [profile, setProfile] = useState<CustomerRow | null>(null);
  const [ordersFor, setOrdersFor] = useState<CustomerRow | null>(null);
  const [blockFor, setBlockFor] = useState<CustomerRow | null>(null);
  const [unblockFor, setUnblockFor] = useState<CustomerRow | null>(null);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((r) => r.email.toLowerCase().includes(t) || r.name.toLowerCase().includes(t));
  }, [rows, q]);

  const exportCsv = () => downloadCsv("cb16-customers.csv", filtered.map((c) => ({
    email: c.email,
    name: c.name,
    phone: c.phone || "",
    city: c.city || "",
    state: c.state || "",
    customerType: c.type === "guest" ? "Guest" : "Registered",
    registeredAt: c.registeredAt || "",
    lastLoginAt: c.lastLoginAt || "",
    orders: c.orders,
    spend: c.spend,
    lastOrderAt: c.lastOrderAt || "",
    status: c.isBlocked ? "Blocked" : "Active",
    blockedAt: c.blockedAt || "",
    blockedBy: c.blockedByEmail || "",
    blockReason: c.blockReason || "",
  })));

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Customers" subtitle={`${rows.length} unique customers (from orders)`} action={
        <button onClick={exportCsv} className="border border-black/15 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] hover:border-black/50">Export CSV</button>
      }/>
      <div className="border border-black/10 bg-white p-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name" className="w-full border border-black/15 px-3 py-2 text-sm outline-none focus:border-black/50"/>
      </div>
      <div className="overflow-x-auto border border-black/10 bg-white">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-black/10 text-left text-[10px] font-semibold uppercase tracking-[0.2em] text-black/50">
              <th className="px-3 py-2">Customer</th>
              <th className="px-3 py-2">Phone</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">City</th>
              <th className="px-3 py-2">State</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Registered</th>
              <th className="px-3 py-2">Last login</th>
              <th className="px-3 py-2">Orders</th>
              <th className="px-3 py-2">Lifetime spend</th>
              <th className="px-3 py-2">Last order</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.email} className="border-b border-black/5">
                <td className="px-3 py-2">
                  <div className="font-medium text-black">{c.name || DASH}</div>
                  <div className="text-[11px] text-black/50">{c.email}</div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-black/80">{fmtPhone(c.phone)}</td>
                <td className="px-3 py-2"><StatusBadge blocked={c.isBlocked} /></td>
                <td className="px-3 py-2 text-black/80">{c.city || DASH}</td>
                <td className="px-3 py-2 text-black/80">{c.state || DASH}</td>
                <td className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-black/60">
                  {c.type === "guest" ? "Guest" : "Registered"}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-black/70">{fmtDate(c.registeredAt)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-black/70">{fmtDate(c.lastLoginAt)}</td>
                <td className="px-3 py-2 text-black/80">{c.orders}</td>
                <td className="px-3 py-2 text-black/80">{money(c.spend)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-black/70">{fmtDate(c.lastOrderAt)}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <ActionButton onClick={() => setProfile(c)}>View profile</ActionButton>
                    <ActionButton onClick={() => setOrdersFor(c)}>View orders</ActionButton>
                    <ActionButton onClick={() => copyText(c.email, "Email")}>Copy email</ActionButton>
                    {c.phone ? (
                      <ActionButton onClick={() => copyText(c.phone, "Phone number")}>Copy phone</ActionButton>
                    ) : null}
                    {!c.isBlocked ? (
                      <ActionButton onClick={() => setBlockFor(c)}>Block customer</ActionButton>
                    ) : (
                      <ActionButton onClick={() => setUnblockFor(c)}>Unblock customer</ActionButton>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={12} className="px-3 py-10 text-center text-sm text-black/50">No customers yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {profile && <ProfileDrawer customer={profile} money={money} onClose={() => setProfile(null)} />}
      {ordersFor && <OrdersDrawer customer={ordersFor} money={money} onClose={() => setOrdersFor(null)} />}
      {blockFor && (
        <BlockDialog
          customer={blockFor}
          onClose={() => setBlockFor(null)}
          onDone={() => {
            setBlockFor(null);
            setProfile(null);
            reload();
          }}
        />
      )}
      {unblockFor && (
        <UnblockDialog
          customer={unblockFor}
          onClose={() => setUnblockFor(null)}
          onDone={() => {
            setUnblockFor(null);
            setProfile(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function BlockDialog({
  customer,
  onClose,
  onDone,
}: {
  customer: CustomerRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [choice, setChoice] = useState("");
  const [other, setOther] = useState("");
  const [busy, setBusy] = useState(false);
  const reason = choice === "Other" ? other.trim() : choice;

  const submit = async () => {
    setBusy(true);
    try {
      await setCustomerBlocked({
        email: customer.email,
        userId: customer.id,
        blocked: true,
        reason: reason || null,
      });
      logAction({
        entity: "user",
        entityId: customer.id,
        action: "customer.blocked",
        summary: `Blocked ${customer.email}${reason ? ` — ${reason}` : ""}`,
      });
      toast.success("Customer blocked");
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not block customer");
      setBusy(false);
    }
  };

  return (
    <Overlay title="Block customer?" onClose={onClose}>
      <p className="text-sm text-black/70">
        This customer will no longer be able to log into their account or place new orders until the
        account is unblocked.
      </p>
      <div className="mt-4">
        <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-black/40">
          Reason (optional, internal only)
        </label>
        <select
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
          className="mt-1 w-full border border-black/15 px-3 py-2 text-sm outline-none focus:border-black/50"
        >
          <option value="">No reason</option>
          {BLOCK_REASONS.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        {choice === "Other" && (
          <input
            value={other}
            onChange={(e) => setOther(e.target.value)}
            placeholder="Describe the reason"
            className="mt-2 w-full border border-black/15 px-3 py-2 text-sm outline-none focus:border-black/50"
          />
        )}
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <ActionButton onClick={onClose}>Cancel</ActionButton>
        <button
          disabled={busy}
          onClick={submit}
          className="border border-red-600/40 bg-red-600 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white disabled:opacity-50"
        >
          {busy ? "Blocking…" : "Block customer"}
        </button>
      </div>
    </Overlay>
  );
}

function UnblockDialog({
  customer,
  onClose,
  onDone,
}: {
  customer: CustomerRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await setCustomerBlocked({ email: customer.email, userId: customer.id, blocked: false });
      logAction({
        entity: "user",
        entityId: customer.id,
        action: "customer.unblocked",
        summary: `Unblocked ${customer.email}`,
      });
      toast.success("Customer unblocked");
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not unblock customer");
      setBusy(false);
    }
  };
  return (
    <Overlay title="Unblock customer?" onClose={onClose}>
      <p className="text-sm text-black/70">
        This customer will regain access to login and place orders.
      </p>
      <div className="mt-6 flex justify-end gap-2">
        <ActionButton onClick={onClose}>Cancel</ActionButton>
        <button
          disabled={busy}
          onClick={submit}
          className="border border-black bg-black px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white disabled:opacity-50"
        >
          {busy ? "Unblocking…" : "Unblock customer"}
        </button>
      </div>
    </Overlay>
  );
}

function ActionButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="border border-black/15 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-black/70 hover:border-black/50 hover:text-black"
    >
      {children}
    </button>
  );
}

function Overlay({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8">
      <div className="w-full max-w-2xl border border-black/10 bg-white">
        <div className="flex items-center justify-between border-b border-black/10 px-4 py-3">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-black/70">{title}</h2>
          <button onClick={onClose} className="text-[11px] font-semibold uppercase tracking-[0.18em] text-black/50 hover:text-black">Close</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-black/40">{label}</div>
      <div className="mt-1 text-sm text-black/80">{value || DASH}</div>
    </div>
  );
}

function ProfileDrawer({ customer, money, onClose }: { customer: CustomerRow; money: (n: number) => string; onClose: () => void }) {
  return (
    <Overlay title="Customer profile" onClose={onClose}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Name" value={customer.name} />
        <Field label="Email" value={customer.email} />
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-black/40">Status</div>
          <div className="mt-1"><StatusBadge blocked={customer.isBlocked} /></div>
        </div>
        <Field label="Phone" value={fmtPhone(customer.phone)} />
        <Field label="Customer type" value={customer.type === "guest" ? "Guest" : "Registered"} />
        <Field label="City" value={customer.city} />
        <Field label="State" value={customer.state} />
        <Field label="Registered" value={fmtDate(customer.registeredAt)} />
        <Field label="Last login" value={fmtDate(customer.lastLoginAt)} />
        <Field label="Orders" value={String(customer.orders)} />
        <Field label="Lifetime spend" value={money(customer.spend)} />
        <Field label="Last order" value={fmtDate(customer.lastOrderAt)} />
        <Field label="Customer ID" value={customer.id} />
        {customer.isBlocked && (
          <>
            <Field label="Blocked by" value={customer.blockedByEmail} />
            <Field label="Blocked date" value={fmtDate(customer.blockedAt)} />
            <Field label="Block reason" value={customer.blockReason} />
          </>
        )}
      </div>
    </Overlay>
  );
}

function OrdersDrawer({ customer, money, onClose }: { customer: CustomerRow; money: (n: number) => string; onClose: () => void }) {
  const { orders, loading, error } = useAdminOrders();
  const mine = useMemo(
    () => orders.filter((o) => (o.email ?? "").toLowerCase() === customer.email.toLowerCase()),
    [orders, customer.email],
  );
  return (
    <Overlay title={`Orders — ${customer.email}`} onClose={onClose}>
      {loading && <p className="text-sm text-black/50">Loading orders…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!loading && !error && mine.length === 0 && <p className="text-sm text-black/50">No orders for this customer.</p>}
      {mine.length > 0 && (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-black/10 text-left text-[10px] font-semibold uppercase tracking-[0.2em] text-black/50">
              <th className="px-2 py-2">Order</th>
              <th className="px-2 py-2">Placed</th>
              <th className="px-2 py-2">Status</th>
              <th className="px-2 py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {mine.map((o) => (
              <tr key={o.rowId} className="border-b border-black/5">
                <td className="px-2 py-2 text-black/80">{o.id}</td>
                <td className="px-2 py-2 whitespace-nowrap text-black/70">{fmtDate(o.createdAt)}</td>
                <td className="px-2 py-2 text-black/70">{o.status}</td>
                <td className="px-2 py-2 text-black/80">{money(Number(o.pricing?.total ?? 0))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Overlay>
  );
}
